import 'client-only';

import { generateQueryString } from '@curology/site-tools';
import { useRouter as useNextRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import COOKIE_KEYS from '@/consts/cookieKeys';
import { flushEvents, getDeviceIdQueryObj } from '@/utils/analytics';
import cookie from '@/utils/cookies/client';
import {
  flushFeatureTracking,
  closeFeatureFlagClient,
} from '@/utils/featureFlags/client';

export const useRouter = () => {
  const nextRouter = useNextRouter();
  const { push: pushBase } = nextRouter;

  const canTrack = () => !cookie.has(COOKIE_KEYS.hasTrackingOptedOut);

  /**
   * Navigate away from the app to an external URL.
   * @param href
   * @param includeQueryString Whether or not to include the device ID query string.
   */
  const away = useCallback((href: string, includeQueryString = true) => {
    if (typeof window === 'undefined') {
      return;
    }
    const queryString =
      includeQueryString && canTrack() ? getDeviceIdQueryObj() : {};

    // add the flag isFromNextJsApp to the query string
    queryString.isFromNextJsApp = 'true';

    flushEvents().then(() => {
      closeFeatureFlagClient(() => {
        window.location.assign(`${href}${generateQueryString(queryString)}`);
      });
    });
  }, []);

  const push: typeof pushBase = useCallback(
    (href, options = {}) => {
      flushFeatureTracking(() => {
        pushBase(href, options);
      });
    },
    [pushBase],
  );

  return useMemo(
    () => ({
      ...nextRouter,
      push,
      away,
    }),
    [away, nextRouter, push],
  );
};
